import { useContext } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { NavigationBackIcon } from '@arcade/web/icons/react'
import { NavigationCloseIcon } from '@arcade/web/icons/react'
import { breakpoints, Button, spacing } from '@cash-design-system/react'

import { ModalActionContext } from './ModalActionContext'

const BackButtonWrapper = styled.div<{ variant?: ModalVariant }>`
  height: 2rem;

  ${({ variant, theme: { spacing } }) =>
    variant === 'receipt'
      ? css`
          padding: ${spacing.m};
          ${breakpoints.tablet} {
            margin-left: -${spacing.xs};
            padding: 1.25rem 2rem 0 2rem;
          }
        `
      : css`
          margin-left: -${spacing.xs};
        `}
`

const CloseButtonWrapper = styled.div<{ variant?: ModalVariant }>`
  ${({ variant, theme: { spacing } }) =>
    variant === 'receipt'
      ? css`
          padding: ${spacing.m};
          ${breakpoints.tablet} {
            padding: 2.5rem 2rem 0 2.5rem;
          }
        `
      : css`
          margin-right: -${spacing.xs};
        `}
`

const PushIconContainer = styled.div`
  color: ${({ theme }) => theme.colors.icon};

  ${breakpoints.tablet} {
    > svg {
      margin-left: ${spacing.s};
    }
  }
`
export const ModalActionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
`

export type ModalVariant = 'default' | 'receipt' | 'fullscreen'

export const ModalCloseAction = ({ variant, onClose, ...props }: { variant?: ModalVariant; onClose?: () => void }) => {
  const { t } = useTranslation()
  return (
    <CloseButtonWrapper variant={variant}>
      <Button
        aria-label={t('Close modal', { context: 'aria label on a button' })}
        onClick={onClose}
        variant="tertiary"
        size="icon"
        {...props}
      >
        <NavigationCloseIcon size={24} variant="standard" />
      </Button>
    </CloseButtonWrapper>
  )
}
export const ModalBackAction = ({ variant, onBack }: { variant?: ModalVariant; onBack?: () => void }) => {
  const { t } = useTranslation()
  if (onBack == null) {
    return null
  }
  return (
    <BackButtonWrapper variant={variant}>
      <Button
        aria-label={t('Go back', { context: 'aria label to go back to the last screen' })}
        onClick={onBack}
        variant="tertiary"
        size="icon"
      >
        <PushIconContainer>
          <NavigationBackIcon />
        </PushIconContainer>
      </Button>
    </BackButtonWrapper>
  )
}

export const ModalActions = ({
  variant,
  onClose,
  onBack,
}: {
  variant?: ModalVariant
  onClose?: () => void
  onBack?: () => void
}) => {
  const { actions: actionOverride } = useContext(ModalActionContext)
  const defaultActions = (
    <ModalActionHeader>
      <ModalCloseAction variant={variant} onClose={onClose} />
      <ModalBackAction variant={variant} onBack={onBack} />
    </ModalActionHeader>
  )
  const actions = actionOverride !== undefined ? actionOverride : defaultActions

  return actions
}
